import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import debounce from "lodash/debounce";
import ListItem from "./ListItem";
import DroppableArea from "./DroppableArea";
import {
  TbLayoutSidebarLeftCollapseFilled,
  TbLayoutSidebarRightCollapseFilled,
} from "react-icons/tb";
import {
  updateL1Property,
  getL1SheetProperty,
  selectTodoDescriptionsProxy,
} from "../WorkbookSlice";
import FilterItem from "./FilterItem";
import ObjectId from "bson-objectid";
import { getSheetControls } from "../WorkbookControllingSlice";
import { LuFileSpreadsheet } from "react-icons/lu";
import { FiFilter } from "react-icons/fi";
import { BiEdit } from "react-icons/bi";
import Format from "./Format";

const GRID_COLUMNS = 24;

export default function Dashboard({ currentSheet }) {
  // // console.log(currentSheet);
  const _id = new ObjectId().toHexString();
  const dispatch = useDispatch();
  const SheetNameArray = useSelector(selectTodoDescriptionsProxy);
  // // console.log(SheetNameArray);
  const dashboardSheetID = currentSheet.Sheetid;
  const droppedSheetArray =
    useSelector((state) =>
      getL1SheetProperty(state, dashboardSheetID, "SheetsArray")
    ) || [];
  const reduxheaderVisibilityArray =
    useSelector((state) =>
      getL1SheetProperty(state, dashboardSheetID, "headerVisibility")
    ) || [];

  const isAnimataionOff = useSelector(
    getSheetControls({ Sheetid: dashboardSheetID, key: "isAnimataionOff" })
  );
  // // console.log(isAnimataionOff);

  const leftSidebar = useRef(null);
  const droppableAreaRef = useRef(null);
  const [droppedItems, setDroppedItems] = useState(
    droppedSheetArray.length ? droppedSheetArray : []
  );
  const [sidebarWidth, setSidebarWidth] = useState(100);
  const [isAsideCollapsed, setIsAsideCollapsed] = useState(false);
  const [droppableWidth, setDroppableWidth] = useState(0);
  const [gridSize, setGridSize] = useState(25);
  // // console.log(gridSize);
  const [tab, setTab] = useState("sheets");
  // // console.log(tab);
  const [headerVisibilityArray, setHeaderVisibilityArray] = useState(
    reduxheaderVisibilityArray || []
  );
  // // console.log(headerVisibilityArray);
  const tabsArray = [
    { lable: "Sheets", value: "sheets", icon: <LuFileSpreadsheet /> },
    { lable: "Filters", value: "filters", icon: <FiFilter /> },
    {
      lable: "Format",
      value: "format",
      icon: <BiEdit className="text-base" />,
    },
  ];
  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, dashboardSheetID, "customization")
  );
  const { dashBgColor, grid } = customizationObject
    ? customizationObject
    : { dashBgColor: "#ffffff", grid: true };

  useEffect(() => {
    setDroppedItems(droppedSheetArray);
  }, []);
  const updateDimensions = () => {
    if (leftSidebar.current) {
      const { width } = leftSidebar.current.getBoundingClientRect();
      setSidebarWidth(width);
    }
    if (droppableAreaRef.current) {
      const droppableWidth =
        droppableAreaRef.current.getBoundingClientRect().width;
      setDroppableWidth(droppableWidth);
      setGridSize(droppableWidth / GRID_COLUMNS);
    }
  };
  useEffect(() => {
    updateDimensions(); // Initial dimensions
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [isAsideCollapsed]);
  // Debounced version of updateL1Property
  const debouncedUpdateL1Property = useRef(
    debounce((dashboardSheetID, droppedItems, headerVisibilityArray) => {
      // console.log(droppedItems);
      dispatch(
        updateL1Property({
          SheetId: dashboardSheetID,
          key: "SheetsArray",
          value: droppedItems,
        })
      );
      dispatch(
        updateL1Property({
          SheetId: dashboardSheetID,
          key: "headerVisibility",
          value: headerVisibilityArray,
        })
      );
    }, 500)
  ).current;
  useEffect(() => {
    if (dashboardSheetID) {
      debouncedUpdateL1Property(
        dashboardSheetID,
        droppedItems,
        headerVisibilityArray
      );
    }
  }, [dashboardSheetID, droppedItems, headerVisibilityArray]);

  useEffect(() => {
    // console.log(reduxheaderVisibilityArray);
    setHeaderVisibilityArray(reduxheaderVisibilityArray);
  }, []);

  const updateItemPosition = (Sheetid, x, y, quillContent) => {
    // console.log(Sheetid);
    // console.log(quillContent);
    setDroppedItems((prevItems) =>
      prevItems.map((item) =>
        item.Sheetid === Sheetid ? { ...item, x, y, quillContent } : item
      )
    );
  };

  const updateItemSize = (Sheetid, width, height, quillContent) => {
    // console.log(quillContent);
    setDroppedItems((prevItems) =>
      prevItems.map((item) =>
        item.Sheetid === Sheetid
          ? { ...item, width, height, quillContent }
          : item
      )
    );
  };

  const handleDrop = (
    Sheetid,
    snappedX,
    snappedY,
    SheetName,
    width,
    height,
    quillContent,
    isParagraph,
    SheetVizType,
    dataSourceID
  ) => {
    // console.log(quillContent);

    setDroppedItems((prevItems) => [
      ...prevItems,
      {
        Sheetid,
        x: snappedX,
        y: snappedY,
        SheetName,
        width,
        height,
        quillContent,
        isParagraph,
        SheetVizType,
        dataSourceID,
      },
    ]);
  };

  const handleRemoveItem = (Sheetid) => {
    setDroppedItems((prevItems) =>
      prevItems.filter((item) => item.Sheetid !== Sheetid)
    );
  };

  const handleTitle = (e, Sheetid) => {
    // // console.log(Sheetid);
    const newTitle = e.target.value; // Get the new Title from the event
    setDroppedItems((prevItems) =>
      prevItems.map((item) =>
        item.Sheetid === Sheetid ? { ...item, Title: newTitle } : item
      )
    );
  };

  return (
    <main className="w-full h-[86.2vh] flex items-center justify-between p-0">
      {isAsideCollapsed ? (
        <aside className="flex border border-gray-200 rounded h-full py-2 w-[2%] items-start justify-center">
          <TbLayoutSidebarRightCollapseFilled
            className="cursor-pointer"
            onClick={() => setIsAsideCollapsed(!isAsideCollapsed)}
          />
        </aside>
      ) : (
        <aside
          ref={leftSidebar}
          className="border border-gray-200 rounded h-full py-2 w-[12vw] min-w-[12vw] border-r-0"
        >
          <div className="flex flex-col items-start justify-center gap-1 w-full px-2 font-light">
            <header className="font-medium flex items-center justify-between w-full">
              {tabsArray.map((itemTab) => {
                if (tab === itemTab.value) {
                  return (
                    <button
                      className="flex items-center gap-1 border hover:border-gray-400 rounded text-sm p-1"
                      onClick={() => setTab(itemTab.value)}
                    >
                      {itemTab.lable}
                      {itemTab.icon}
                    </button>
                  );
                } else if (tab !== itemTab.value) {
                  return (
                    <button onClick={() => setTab(itemTab.value)}>
                      {itemTab.icon}
                    </button>
                  );
                }
              })}
              <TbLayoutSidebarLeftCollapseFilled
                className="cursor-pointer"
                onClick={() => setIsAsideCollapsed(!isAsideCollapsed)}
              />
            </header>
            {tab === "sheets" ? (
              <>
                <div className="flex flex-col gap-1 w-full font-light">
                  {SheetNameArray.map((item) => {
                    // console.log(item);
                    if (
                      item.sheetType !== "dashboard" ||
                      item.sheetType === "chart"
                    ) {
                      return (
                        <ListItem
                          key={item.ID}
                          Sheetid={item.ID}
                          isParagraph={false}
                          SheetVizType={item.VizType}
                          SheetName={item.SheetName}
                          droppableWidth={droppableWidth}
                          dataSourceID={item.dataSourceID}
                        />
                      );
                    }
                  })}
                </div>
                <div className="flex flex-col items-start justify-center gap-1 w-full font-light">
                  <h1 className="font-medium flex items-center justify-between w-full">
                    Objects
                  </h1>
                  <ListItem
                    Sheetid={_id}
                    isParagraph={true}
                    SheetName={"Paragraph"}
                    SheetVizType={"Text"}
                    droppableWidth={droppableWidth}
                    dataSourceID={null}
                  ></ListItem>
                </div>
              </>
            ) : tab === "filters" ? (
              <div className="flex flex-col gap-1 w-full font-light">
                <FilterItem
                  dashboardSheetID={dashboardSheetID}
                  isFilterVisibleInDashboard={true}
                  droppedSheetsArray={droppedItems}
                  currentSheetType={"dashboard"}
                  dashboardType={"makeDashboard"}
                />
              </div>
            ) : tab === "format" ? (
              <div className="flex flex-col gap-1 w-full font-light">
                <Format currentSheetId={dashboardSheetID} />
              </div>
            ) : null}
          </div>
        </aside>
      )}

      <div ref={droppableAreaRef} className="flex-grow h-full">
        <DroppableArea
          items={droppedSheetArray}
          updateItemPosition={updateItemPosition}
          updateItemSize={updateItemSize}
          handleDrop={handleDrop}
          sidebarWidth={sidebarWidth}
          isAsideCollapsed={isAsideCollapsed}
          handleRemoveItem={handleRemoveItem}
          handleTitle={handleTitle}
          gridSize={gridSize}
          gridColumns={GRID_COLUMNS}
          dashboardSheetID={dashboardSheetID}
          headerVisibilityArray={headerVisibilityArray}
          setHeaderVisibilityArray={setHeaderVisibilityArray}
          isAnimataionOff={isAnimataionOff}
          dashBgColor={dashBgColor}
          grid={grid}
        />
      </div>
    </main>
  );
}
